import React, { useEffect, useState } from "react";
import { Box, FormControlLabel, FormGroup, Switch, Typography } from "@material-ui/core";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { useSelector } from "react-redux";
import { getEmailSchedulerDefinitions } from "store/features/emailScheduler/definition";
import { FrequencyType, SchedulerExceptionForm } from "validation";
import { Select, TextField } from "components";
import { useSelectOptions } from "hooks";

export const weekDays = [
    { translation: "Monday", field: "MONDAY" },
    { translation: "Tuesday", field: "TUESDAY" },
    { translation: "Wednesday", field: "WEDNESDAY" },
    { translation: "Thursday", field: "THURSDAY" },
    { translation: "Friday", field: "FRIDAY" },
    { translation: "Saturday", field: "SATURDAY" },
    { translation: "Sunday", field: "SUNDAY" },
];

const exceptionFrequency = [
    { translation: "Every day", field: "EVERY_DAY" },
    { translation: "Week", field: "EVERY_WEEK" },
];

export const ScheduleSection = () => {
    const { control, errors } = useFormContext<SchedulerExceptionForm>();
    const [isTemplateDisabled, setDisableTemplate] = useState(false);
    const { templates, statuses } = useSelector(getEmailSchedulerDefinitions);
    const templateDefinitions = useSelectOptions(templates);
    const days = useSelectOptions(weekDays);
    const frequencies = useSelectOptions(exceptionFrequency);
    const statusDetails = useSelectOptions(statuses);
    const [frequency, setFrequency] = useState<FrequencyType>("EVERY_DAY");
    const fields = useWatch({
        control,
        name: ["day", "materialRequest"],
    });

    // if day is set update frequency
    useEffect(() => {
        if (fields.day) {
            return setFrequency("EVERY_WEEK");
        }

        setFrequency("EVERY_DAY");
    }, [fields.day]);

    useEffect(() => {
        if (!fields.materialRequest) {
            return setDisableTemplate(true);
        }

        setDisableTemplate(false);
    }, [fields.materialRequest]);

    useEffect(() => {
        if (isTemplateDisabled) {
            control.setValue("template", "");
        }
    }, [control, isTemplateDisabled]);

    return (
        <Box mb={4} display="flex">
            <Box flex={0.4}>
                <Typography component="div" variant="subtitle2">
                    <Box fontWeight="fontWeightBold">Schedule</Box>
                </Typography>
                <FormGroup row={true}>
                    <Controller
                        name="materialRequest"
                        type="checkbox"
                        control={control}
                        render={({ onChange, value }) => {
                            return (
                                <FormControlLabel
                                    label="Material request"
                                    labelPlacement="end"
                                    control={
                                        <Switch
                                            color="primary"
                                            checked={value}
                                            onChange={(event, checked) => onChange(checked)}
                                        />
                                    }
                                />
                            );
                        }}
                    />
                </FormGroup>
                <Box display="flex" pt={1} mb={2}>
                    <Select
                        name="frequency"
                        title="Frequency"
                        options={frequencies}
                        onChange={(onChange, value) => {
                            setFrequency(value.target.value);
                            onChange(value);
                        }}
                    />

                    {frequency === "EVERY_WEEK" && <Select name="day" title="Day" options={days} error={errors.day} />}

                    <Controller
                        name="time"
                        control={control}
                        as={
                            <TextField
                                label="Time"
                                type="time"
                                inputProps={{
                                    step: 300, // 5 min
                                }}
                                error={!!errors.time}
                                helperText={errors.time?.message}
                            />
                        }
                    />
                </Box>
                <Box display="flex" pt={1} mb={2}>
                    <Select
                        disabled={isTemplateDisabled}
                        name="template"
                        title="Template"
                        options={templateDefinitions}
                        error={errors.template}
                    />

                    <Select name="status" title="Set status" error={errors.status} options={statusDetails} />

                    <Controller
                        name="lookHeadDays"
                        control={control}
                        as={
                            <TextField
                                label="Look ahead day(s)"
                                type="number"
                                error={!!errors.lookHeadDays}
                                helperText={errors.lookHeadDays?.message}
                            />
                        }
                    />
                </Box>
            </Box>
        </Box>
    );
};
